import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux'
import { GlobalStore } from '../../../store';
import { loadAdditionalQueueData } from '../../../actions/administratorActions';
import { openShowQueueModal, openHideQueueModal, openDeleteQueueModal, openRenameQueueModal } from '../../../actions/modalActions';
import User from '../../../models/User';
import Queue from '../../../models/Queue';
import TeachersViewComponent from './Teachers/Teachers';
import AssistantsViewComponent from './Assistants/Assistants';

export default (): JSX.Element | null => {

  const user = useSelector<GlobalStore, User | null>(store => store.user);
  const queues = useSelector<GlobalStore, Queue[]>(store => store.queues.queueList)
  .sort((queue1: Queue, queue2: Queue) => queue1.name < queue2.name ? -1 : 1);

  const [selectedQueue, setSelectedQueue] = useState(null as Queue | null);
  const [selectedQueueName, setSelectedQueueName] = useState(null as string | null);
  const [queueFilter, setQueueFilter] = useState('');

  function selectQueue(queueName: string): void {
    if (user !== null) {
      setSelectedQueue(queues.filter(q => q.name === queueName)[0] || null);
      setSelectedQueueName(queueName);
      dispatch(loadAdditionalQueueData(queueName, user.token));
    }
  }

  useEffect(() => {
    if (selectedQueueName !== null) {
      setSelectedQueue(queues.filter(q => q.name === selectedQueueName)[0] || null);
    }
  }, [queues, selectedQueueName]);

  const dispatch = useDispatch();

  return (
    queues.length === 0 || user === null
      ? null
      : <>
          <div className="row mb-2">
            <h2 className="col-12 p-0">Edit queue</h2>
            <p className="col-12 p-0">Choose which queue you wish to edit</p>
            {user.isAdministrator ? (
              <div className="col-12 p-0" style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search queue…"
                  value={queueFilter}
                  onChange={e => setQueueFilter(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const match = queues.find(q => q.name.toLowerCase().startsWith(queueFilter.toLowerCase()));
                      if (match) { selectQueue(match.name); setQueueFilter(match.name); }
                    }
                  }}
                />
                {queueFilter.length > 0 && selectedQueue?.name !== queueFilter && (
                  <div style={{
                    position: 'absolute', zIndex: 1000, background: 'white',
                    border: '1px solid #ccc', borderRadius: '4px', width: '100%',
                    maxHeight: '260px', overflowY: 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                  }}>
                    {queues
                      .filter(q => q.name.toLowerCase().includes(queueFilter.toLowerCase()))
                      .map(q => (
                        <button
                          key={q.name}
                          className="dropdown-item"
                          type="button"
                          onClick={() => { selectQueue(q.name); setQueueFilter(q.name); }}
                        >
                          {q.name}
                        </button>
                      ))
                    }
                  </div>
                )}
                {selectedQueue && <small className="text-muted">Selected: <strong>{selectedQueue.name}</strong></small>}
              </div>
            ) : (
              <div className="col-12 p-0" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {user.teacherIn.map(name => (
                  <button
                    key={name}
                    type="button"
                    className={`btn btn-sm ${selectedQueue?.name === name ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => selectQueue(name)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {
            selectedQueue === null
              ? null
              : <>
                  <div className="row mb-5">
                    {
                      selectedQueue.hiding
                        ? <button
                            className="btn btn-success mb-2 mb-lg-0 mr-0 mr-lg-2"
                            onClick={() => dispatch(openShowQueueModal(selectedQueue.name))}>
                              Reveal queue
                          </button>
                        : <button
                            className="btn btn-warning mb-2 mb-lg-0 mr-0 mr-lg-2"
                            onClick={() => dispatch(openHideQueueModal(selectedQueue.name))}>
                              Hide queue
                          </button>
                    }
                    <button
                      className={`btn btn-danger mb-2 mb-lg-0 ${user.isAdministrator ? 'mr-0 mr-lg-2': null}`}
                      onClick={() => dispatch(openDeleteQueueModal(selectedQueue.name))}>
                        Delete queue
                    </button>
                    {
                      user.isAdministrator
                        ? <button
                            className="btn btn-primary mb-2 mb-lg-0"
                            onClick={() => dispatch(openRenameQueueModal(selectedQueue.name))}>
                              Rename queue
                          </button>
                        : null
                    }
                  </div>
                  <div className="row">
                    <div className="col-12 col-lg-6">
                      <TeachersViewComponent queue={selectedQueue} />
                    </div>
                    <div className="col-12 col-lg-6 mt-5 mt-lg-0">
                      <AssistantsViewComponent queue={selectedQueue} />
                    </div>
                  </div>
                </>
          }
          {user.isTeacher() && (
            <p className="text-muted mt-3" style={{ fontSize: '0.85em' }}>
              Adding and removing teaching assistants can also be done via the REST API — see the <Link to="/api">API</Link> tab.
            </p>
          )}
        </>
  );
};
